import React, { useEffect, useMemo, useState } from "react";

import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import { enrollSimulation } from "src/redux/actions/coaching/learners";

import { SimulationSummaryComponent } from "src/pages/coaching/components/simulation/view";
import { SimulationContext, SimulationContextProps } from "src/pages/coaching/components/simulation/view/context";

import userRoleService from "src/utils/userRoles.service";
import PermissionDenined from "src/components/permissionDenied";
import {
  COACHING_EXPLORE_PATH,
  getLearnerBackLabel,
  resolveLearnerBackPath,
  buildOriginNavigationState,
} from "src/pages/coaching/utils/manageNavigation";

import _ from 'lodash';
import styles from "./view.module.scss";
import { fetchPublicSimulation } from "src/redux/actions/coaching/simulation";
import { LearningRecordSimulation } from "src/types/coaching/learners";

interface EnrollSimulationState {
  simulation: LearningRecordSimulation | null;
}

export const ViewExploreSimulationPage: React.FC<object> = () => {
  const dispatch = useDispatch();
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const simulationId = params.id;
  const backPath = (location.state as { from?: string } | null)?.from;
  const exploreFrom = backPath ?? COACHING_EXPLORE_PATH;
  const backLabel = useMemo(
    () => getLearnerBackLabel(exploreFrom),
    [exploreFrom],
  );

  const simulation = useSelector((state: any) =>
    _.get(state.coaching, `simulations.simulation[${simulationId}].item`),
  );

  const loading = useSelector((state: any) =>
    _.get(state.coaching, `simulations.simulation[${simulationId}].loading`),
  );
  

  const enrollingSimulation = useSelector((state: any) => {
    const enrolling = _.get(state.coaching, `simulations.simulation[${simulationId}].enroll.enrolling`);
    return enrolling;
  });

  const simulationEnrolled = useSelector((state: any) => {
    return _.get(state.coaching, `simulations.simulation[${simulationId}].enroll.enrolled`);
  });

  const simulationEnrolledId = useSelector((state: any) => {
    return _.get(state.coaching, `simulations.simulation[${simulationId}].enroll.id`);
  });

  const [courseContext, setCourseContext] = React.useState<SimulationContextProps>({
    id: simulationId,
    view: "summary",
    showLearnerAttemptsDropdown: true,
  });

  const [publicCourse, setPublicCourse] = useState<EnrollSimulationState>({
    simulation: null,
  }); 

  const handleBackClicked = () => {
    navigate(resolveLearnerBackPath(backPath, COACHING_EXPLORE_PATH));
  };

  const handleEnrollSimulation = () => {
    if (simulation) {
      dispatch(enrollSimulation({course: simulation.id}));
    }
  };

  useEffect(() => {
    if (simulationId && !simulation) {
      dispatch(fetchPublicSimulation({id: simulationId}));
    }
  
    setCourseContext((prev) => {
      return {
        ...prev,
        id: simulationId,
        view: "summary",
      }
    });
  }, []);

  useEffect(() => {
    setCourseContext((prev) => {
      return {
        ...prev,
        onBack: handleBackClicked,
        enrollSimulation: handleEnrollSimulation
      }
    });

    if(!simulation) {
      return;
    }

    setPublicCourse((prev) => {
      return {
        ...prev,
        simulation: {
          ...simulation,
          id: simulation.id!,
          simulation: simulation,
          sessions: [],
          status: "NOT_ENROLLED",
        },
      }
    });

  }, [simulation]);

  useEffect(() => {
    if (simulationEnrolled && simulationEnrolledId) {
      navigate(`/coaching/simulations/${simulationEnrolledId}`, {
        state: buildOriginNavigationState(COACHING_EXPLORE_PATH),
      });
    }
  }, [simulationEnrolled, simulationEnrolledId, exploreFrom]);

  useEffect(() => {
    setCourseContext((prev) => {
      return {
        ...prev,
        enrollingSimulation: enrollingSimulation
      }
    });
  }, [enrollingSimulation]);

  if(!userRoleService.canAccessCoaching()) {
    return <PermissionDenined className={styles.permissionDenied} />;
  }

  if ((loading && simulation?.id != simulationId) || !simulation) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.back} onClick={handleBackClicked}>
          <i className="paperflite-left-arrow" />
          <span>{backLabel}</span>
        </div>
        <div className={styles.loading}>Loading simulation...</div>
      </div>
    );
  }
  

  return (
    <SimulationContext.Provider value={courseContext}>
      {publicCourse.simulation && (
        <SimulationSummaryComponent simulation={publicCourse.simulation} />
      )}
    </SimulationContext.Provider>
  );
}
